import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2,
  Snackbar,
  Alert,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
} from "@mui/material";
import AxiosInstance from "../../../utils/Axios.ts";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import Tour from "../TourInterface.ts";

interface BulkActionOverlayProps {
  open: boolean;
  onClose: () => void;
  rowSelectionModel: GridRowSelectionModel;
  tours: Tour[];
  setTours: (tours: Tour[]) => void;
}

const colorChoices: Record<number, string> = {
  0: "white",
  1: "red",
  2: "orange",
  3: "green",
  4: "blue",
  5: "purple",
  6: "pink",
  7: "brown",
  8: "yellow",
  9: "seagreen",
  10: "lightblue",
};

const BulkActionOverlay: React.FC<BulkActionOverlayProps> = ({
  open,
  onClose,
  rowSelectionModel,
  tours,
  setTours,
}) => {
  const [updateCheckIn, setUpdateCheckIn] = useState<boolean>(false);
  const [checkInMinutes, setCheckInMinutes] = useState<number>(0);
  const [updateDeparture, setUpdateDeparture] = useState<boolean>(false);
  const [departureMinutes, setDepartureMinutes] = useState<number>(0);
  const [updateArrival, setUpdateArrival] = useState<boolean>(false);
  const [arrivalMinutes, setArrivalMinutes] = useState<number>(0);
  const [updateColor, setUpdateColor] = useState<boolean>(false);
  const [colorChoice, setColorChoice] = useState<number>(0);
  const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "error" as "error" | "success",
    });

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const handleBulkAction = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (rowSelectionModel.length === 0) {
      setSnackbar({
        open: true,
        message: "No rows selected.",
        severity: "error",
      });
      return;
    }

    try {
      const updates = rowSelectionModel.map((rowId) => {
        // Find the row data in the state (e.g., tours)
        const rowData = tours.find((row) => row.id === rowId);

        if (!rowData) {
          throw new Error(`Row with ID ${rowId} not found.`);
        }

        const updatedRow: Record<string, any> = {};

        // Function to format time as HH:mm:ss
        const formatTime = (date: Date) =>
          date.toLocaleTimeString('en-GB', { hour12: false });

        if (updateCheckIn) {
          const currentCheckIn = new Date(`1970-01-01T${rowData.check_in_time}`);
          currentCheckIn.setMinutes(currentCheckIn.getMinutes() + checkInMinutes);
          updatedRow.check_in_time = formatTime(currentCheckIn);
        }

        if (updateDeparture) {
          const currentDeparture = new Date(`1970-01-01T${rowData.time_from}`);
          currentDeparture.setMinutes(currentDeparture.getMinutes() + departureMinutes);
          updatedRow.time_from = formatTime(currentDeparture);
        }

        if (updateArrival) {
          const currentArrival = new Date(`1970-01-01T${rowData.time_to}`);
          currentArrival.setMinutes(currentArrival.getMinutes() + arrivalMinutes);
          updatedRow.time_to = formatTime(currentArrival);
        }

        if (updateColor) {
          updatedRow.color_choice = colorChoice;
        }

        return { id: rowId, updates: updatedRow };
      });

      // Send updates to the server for all rows
      await Promise.all(
        updates.map(({ id, updates }) =>
          AxiosInstance.patch(`/tour/patch/${id}/`, updates)
        )
      );

      // Update the state with the new data
      const updatedTours = tours.map((tour) => {
        const update = updates.find((u) => u.id === tour.id);
        return update ? { ...tour, ...update.updates } : tour;
      });

      setTours(updatedTours);

      setArrivalMinutes(0);
      setCheckInMinutes(0);
      setDepartureMinutes(0);
      setUpdateArrival(false);
      setUpdateCheckIn(false);
      setUpdateDeparture(false);
      setUpdateColor(false);

      setSnackbar({
        open: true,
        message: "Bulk action applied successfully.",
        severity: "success",
      });
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to apply bulk action.",
        severity: "error",
      });
    }
  };




  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Bulk Actions</DialogTitle>
      <DialogContent>
        <Grid2 container spacing={2} marginTop={2}>
          <Grid2 size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={updateCheckIn}
                  onChange={(e) => setUpdateCheckIn(e.target.checked)}
                />
              }
              label="Update Check In Time"
            />
            {updateCheckIn && (
            <TextField
              label="Check-in Minutes"
              type="number"
              value={checkInMinutes}
              onChange={(e) => setCheckInMinutes(parseInt(e.target.value))}
            />
            )}
          </Grid2>
          <Grid2 size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={updateDeparture}
                  onChange={(e) => setUpdateDeparture(e.target.checked)}
                />
              }
              label="Update Departure Time"
            />
            {updateDeparture && (
            <TextField
              label="Departure Minutes"
              type="number"
              value={departureMinutes}
              onChange={(e) => setDepartureMinutes(parseInt(e.target.value))}
            />
            )}
          </Grid2>
          <Grid2 size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={updateArrival}
                  onChange={(e) => setUpdateArrival(e.target.checked)}
                />
              }
              label="Update Arrival Time"
            />
            {updateArrival && (
              <TextField
                label="Minutes"
                type="number"
                value={arrivalMinutes}
                onChange={(e) => setArrivalMinutes(parseInt(e.target.value))}
              />
            )}
          </Grid2>
          <Grid2 size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={updateColor}
                  onChange={(e) => setUpdateColor(e.target.checked)}
                />
              }
              label="Update Color"
            />
            {updateColor && (
              <Select
                value={colorChoice}
                onChange={(e) => setColorChoice(e.target.value as number)}
              >
                {Object.keys(colorChoices).map((key) => (
                  <MenuItem key={key} value={parseInt(key)}>
                    {colorChoices[parseInt(key)]}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Grid2>
        </Grid2>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleBulkAction} color="primary">
          Apply
        </Button>
      </DialogActions>
      <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
    </Dialog>
  );
};

export default BulkActionOverlay;